/**
 * Whiteboard canvas helpers
 *
 * Normalizes Losi whiteboard payloads and bridges them to the subset of
 * tldraw document records that Spaces currently supports.
 *
 * Primary responsibilities:
 * - Normalize legacy and canonical `canvas_data` payloads
 * - Extract a stable Losi canvas contract from stored tldraw snapshots
 * - Build compatibility-safe tldraw store snapshots for the canvas
 *
 * Dependencies:
 * - @tldraw/utils
 * - types/spaces
 */

import { getIndices } from "@tldraw/utils"

import type {
  WhiteboardAnchor,
  WhiteboardArrowhead,
  WhiteboardCanvasData,
  WhiteboardColor,
  WhiteboardConnection,
  WhiteboardConnectionType,
  WhiteboardDashStyle,
  WhiteboardFillStyle,
  WhiteboardFont,
  WhiteboardPoint,
  WhiteboardShape,
  WhiteboardShapeType,
  WhiteboardSize,
  WhiteboardStoreSnapshot
} from "@/types/spaces"
import {
  WHITEBOARD_ANCHOR_OPTIONS,
  WHITEBOARD_ARROWHEAD_OPTIONS,
  WHITEBOARD_COLOR_OPTIONS,
  WHITEBOARD_CONNECTION_TYPES,
  WHITEBOARD_DASH_STYLE_OPTIONS,
  WHITEBOARD_FILL_STYLE_OPTIONS,
  WHITEBOARD_FONT_OPTIONS,
  WHITEBOARD_SHAPE_TYPES,
  WHITEBOARD_SIZE_OPTIONS
} from "@/types/spaces"

type UnknownRecord = Record<string, unknown>

const DEFAULT_CANVAS_VERSION = 1
const DEFAULT_TEXT_WIDTH = 240
const DEFAULT_NOTE_WIDTH = 220
const DEFAULT_NOTE_HEIGHT = 220
const DEFAULT_BOX_WIDTH = 220
const DEFAULT_BOX_HEIGHT = 120
const DEFAULT_IMAGE_WIDTH = 320
const DEFAULT_IMAGE_HEIGHT = 180
const DEFAULT_ARROW_DELTA = { x: 220, y: 0 }

const WHITEBOARD_COLOR_SET = new Set<string>(WHITEBOARD_COLOR_OPTIONS)
const WHITEBOARD_FILL_STYLE_SET = new Set<string>(WHITEBOARD_FILL_STYLE_OPTIONS)
const WHITEBOARD_DASH_STYLE_SET = new Set<string>(WHITEBOARD_DASH_STYLE_OPTIONS)
const WHITEBOARD_SIZE_SET = new Set<string>(WHITEBOARD_SIZE_OPTIONS)
const WHITEBOARD_FONT_SET = new Set<string>(WHITEBOARD_FONT_OPTIONS)
const WHITEBOARD_SHAPE_TYPE_SET = new Set<string>(WHITEBOARD_SHAPE_TYPES)
const WHITEBOARD_CONNECTION_TYPE_SET = new Set<string>(WHITEBOARD_CONNECTION_TYPES)
const WHITEBOARD_ANCHOR_SET = new Set<string>(WHITEBOARD_ANCHOR_OPTIONS)
const WHITEBOARD_ARROWHEAD_SET = new Set<string>(WHITEBOARD_ARROWHEAD_OPTIONS)

const LEGACY_COLOR_MAP: Record<string, WhiteboardColor> = {
  "#000": "black",
  "#000000": "black",
  "#111": "black",
  "#111111": "black",
  "#1f2937": "black",
  "#374151": "grey",
  "#6b7280": "grey",
  "#9ca3af": "grey",
  "#2563eb": "blue",
  "#3b82f6": "blue",
  "#60a5fa": "light-blue",
  "#93c5fd": "light-blue",
  "#10b981": "green",
  "#22c55e": "light-green",
  "#84cc16": "light-green",
  "#ef4444": "red",
  "#f87171": "light-red",
  "#f59e0b": "orange",
  "#fbbf24": "yellow",
  "#8b5cf6": "violet",
  "#c084fc": "light-violet",
  "#ffffff": "white",
  "#fff": "white"
}

function asRecord(value: unknown): UnknownRecord | null {
  return typeof value === "object" && value !== null
    ? (value as UnknownRecord)
    : null
}

function asFiniteNumber(value: unknown, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback
}

function asOptionalNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

function asOptionalString(value: unknown) {
  if (typeof value !== "string") {
    return undefined
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function normalizeWhiteboardText(value: unknown) {
  const normalized = asOptionalString(value)

  if (!normalized) {
    return undefined
  }

  return normalized
    .replace(/\r\n?/g, "\n")
    .replace(/\\n|\/n/g, "\n")
}

function normalizeEnum<T extends string>(
  value: unknown,
  validValues: Set<string>,
  fallback: T
): T {
  const normalized = asOptionalString(value)?.toLowerCase()
  return normalized && validValues.has(normalized) ? (normalized as T) : fallback
}

function normalizeWhiteboardColor(
  value: unknown,
  fallback: WhiteboardColor = "black"
): WhiteboardColor {
  const normalized = asOptionalString(value)?.toLowerCase()

  if (!normalized) {
    return fallback
  }

  if (WHITEBOARD_COLOR_SET.has(normalized)) {
    return normalized as WhiteboardColor
  }

  return LEGACY_COLOR_MAP[normalized] || fallback
}

function normalizeWhiteboardFillStyle(
  value: unknown,
  fallback: WhiteboardFillStyle = "none"
): WhiteboardFillStyle {
  return normalizeEnum(value, WHITEBOARD_FILL_STYLE_SET, fallback)
}

function normalizeWhiteboardDashStyle(
  value: unknown,
  fallback: WhiteboardDashStyle = "draw"
): WhiteboardDashStyle {
  return normalizeEnum(value, WHITEBOARD_DASH_STYLE_SET, fallback)
}

function normalizeWhiteboardSize(
  value: unknown,
  fallback: WhiteboardSize = "m"
): WhiteboardSize {
  const normalized = asOptionalString(value)?.toLowerCase()

  if (normalized && WHITEBOARD_SIZE_SET.has(normalized)) {
    return normalized as WhiteboardSize
  }

  const width = asOptionalNumber(value)
  if (width !== undefined) {
    if (width <= 1) return "s"
    if (width <= 2) return "m"
    if (width <= 4) return "l"
    return "xl"
  }

  return fallback
}

function normalizeWhiteboardFont(
  value: unknown,
  fallback: WhiteboardFont = "draw"
): WhiteboardFont {
  const normalized = asOptionalString(value)?.toLowerCase()

  if (normalized && WHITEBOARD_FONT_SET.has(normalized)) {
    return normalized as WhiteboardFont
  }

  if (normalized?.includes("mono")) return "mono"
  if (normalized?.includes("serif")) return "serif"
  if (normalized?.includes("sans")) return "sans"

  return fallback
}

function normalizeWhiteboardShapeType(
  value: unknown,
  fallback: WhiteboardShapeType = "rectangle"
): WhiteboardShapeType {
  const normalized = asOptionalString(value)?.toLowerCase()

  if (!normalized) {
    return fallback
  }

  if (WHITEBOARD_SHAPE_TYPE_SET.has(normalized)) {
    return normalized as WhiteboardShapeType
  }

  switch (normalized) {
    case "ellipse":
    case "oval":
      return "circle"
    case "sticky":
    case "sticky_note":
    case "sticky-note":
      return "note"
    default:
      return fallback
  }
}

function normalizeWhiteboardConnectionType(
  value: unknown,
  fallback: WhiteboardConnectionType = "straight"
): WhiteboardConnectionType {
  return normalizeEnum(value, WHITEBOARD_CONNECTION_TYPE_SET, fallback)
}

function normalizeWhiteboardAnchor(
  value: unknown,
  fallback: WhiteboardAnchor = "center"
): WhiteboardAnchor {
  return normalizeEnum(value, WHITEBOARD_ANCHOR_SET, fallback)
}

function normalizeWhiteboardArrowhead(
  value: unknown,
  fallback: WhiteboardArrowhead
): WhiteboardArrowhead {
  return normalizeEnum(value, WHITEBOARD_ARROWHEAD_SET, fallback)
}

function normalizePoint(value: unknown): WhiteboardPoint | null {
  const record = asRecord(value)

  if (record) {
    return {
      x: asFiniteNumber(record.x, 0),
      y: asFiniteNumber(record.y, 0)
    }
  }

  return null
}

function normalizePointList(value: unknown): WhiteboardPoint[] | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }

  if (value.every(item => typeof item === "number")) {
    const points: WhiteboardPoint[] = []

    for (let index = 0; index < value.length; index += 2) {
      points.push({
        x: asFiniteNumber(value[index], 0),
        y: asFiniteNumber(value[index + 1], 0)
      })
    }

    return points.length > 0 ? points : undefined
  }

  const points = value
    .map(item => normalizePoint(item))
    .filter((point): point is WhiteboardPoint => Boolean(point))

  return points.length > 0 ? points : undefined
}

function ensureRecordId(id: string, key: string) {
  return id.trim() || key
}

function normalizeWhiteboardShape(
  key: string,
  value: unknown
): WhiteboardShape | null {
  const record = asRecord(value)

  if (!record) {
    return null
  }

  const id = ensureRecordId(asOptionalString(record.id) || key, key)
  const type = normalizeWhiteboardShapeType(record.type)
  const legacyStroke = asOptionalString(record.stroke)
  const legacyFill = asOptionalString(record.fill)
  const strokeWidth = asOptionalNumber(record.strokeWidth)
  const color = normalizeWhiteboardColor(record.color ?? legacyStroke, "black")
  const fillStyle = normalizeWhiteboardFillStyle(record.fillStyle ?? legacyFill, "none")
  const points =
    normalizePointList(record.points) ||
    (() => {
      const start = normalizePoint(record.start)
      const end = normalizePoint(record.end)

      return start && end ? [start, end] : undefined
    })()

  return {
    id,
    type,
    x: asFiniteNumber(record.x, 0),
    y: asFiniteNumber(record.y, 0),
    width: asOptionalNumber(record.width),
    height: asOptionalNumber(record.height),
    rotation: asOptionalNumber(record.rotation),
    opacity: asOptionalNumber(record.opacity),
    color,
    fillStyle,
    dash: normalizeWhiteboardDashStyle(record.dash, "draw"),
    size: normalizeWhiteboardSize(record.size ?? strokeWidth, "m"),
    font: normalizeWhiteboardFont(record.font ?? record.fontFamily, "draw"),
    textAlign: normalizeEnum(record.textAlign, new Set(["start", "middle", "end"]), "start"),
    align: normalizeEnum(record.align, new Set(["start", "middle", "end"]), "middle"),
    verticalAlign: normalizeEnum(
      record.verticalAlign,
      new Set(["start", "middle", "end"]),
      "middle"
    ),
    url: asOptionalString(record.url),
    fill: legacyFill || fillStyle,
    stroke: legacyStroke || color,
    strokeWidth,
    text: normalizeWhiteboardText(record.text),
    fontSize: asOptionalNumber(record.fontSize),
    fontFamily: asOptionalString(record.fontFamily),
    points,
    start: normalizePoint(record.start) || points?.[0],
    end:
      normalizePoint(record.end) ||
      (points && points.length > 1 ? points[points.length - 1] : undefined),
    bend: asOptionalNumber(record.bend),
    arrowheadStart: normalizeWhiteboardArrowhead(
      record.arrowheadStart,
      "none"
    ),
    arrowheadEnd: normalizeWhiteboardArrowhead(record.arrowheadEnd, "arrow"),
    src: asOptionalString(record.src),
    locked: typeof record.locked === "boolean" ? record.locked : undefined,
    z_index: asOptionalNumber(record.z_index)
  }
}

function normalizeWhiteboardConnection(
  key: string,
  value: unknown
): WhiteboardConnection | null {
  const record = asRecord(value)

  if (!record) {
    return null
  }

  const id = ensureRecordId(asOptionalString(record.id) || key, key)
  const fromShapeId = asOptionalString(record.from_shape_id)
  const toShapeId = asOptionalString(record.to_shape_id)

  if (!fromShapeId || !toShapeId) {
    return null
  }

  const legacyStroke = asOptionalString(record.stroke)
  const strokeWidth = asOptionalNumber(record.strokeWidth)

  return {
    id,
    from_shape_id: fromShapeId,
    to_shape_id: toShapeId,
    type: normalizeWhiteboardConnectionType(record.type, "straight"),
    from_anchor: normalizeWhiteboardAnchor(record.from_anchor, "center"),
    to_anchor: normalizeWhiteboardAnchor(record.to_anchor, "center"),
    color: normalizeWhiteboardColor(record.color ?? legacyStroke, "black"),
    dash: normalizeWhiteboardDashStyle(record.dash, "draw"),
    size: normalizeWhiteboardSize(record.size ?? strokeWidth, "m"),
    text: normalizeWhiteboardText(record.text),
    bend: asOptionalNumber(record.bend),
    arrowheadStart: normalizeWhiteboardArrowhead(
      record.arrowheadStart,
      "none"
    ),
    arrowheadEnd: normalizeWhiteboardArrowhead(record.arrowheadEnd, "arrow"),
    stroke: legacyStroke,
    strokeWidth
  }
}

function normalizeShapeCollection(value: unknown) {
  if (Array.isArray(value)) {
    return Object.fromEntries(
      value
        .map((shape, index) =>
          normalizeWhiteboardShape(`shape-${index + 1}`, shape)
        )
        .filter((shape): shape is WhiteboardShape => Boolean(shape))
        .map(shape => [shape.id, shape])
    )
  }

  const record = asRecord(value)

  if (!record) {
    return {}
  }

  return Object.fromEntries(
    Object.entries(record)
      .map(([key, shape]) => normalizeWhiteboardShape(key, shape))
      .filter((shape): shape is WhiteboardShape => Boolean(shape))
      .map(shape => [shape.id, shape])
  )
}

function normalizeConnectionCollection(value: unknown) {
  if (Array.isArray(value)) {
    return Object.fromEntries(
      value
        .map((connection, index) =>
          normalizeWhiteboardConnection(`connection-${index + 1}`, connection)
        )
        .filter((connection): connection is WhiteboardConnection => Boolean(connection))
        .map(connection => [connection.id, connection])
    )
  }

  const record = asRecord(value)

  if (!record) {
    return {}
  }

  return Object.fromEntries(
    Object.entries(record)
      .map(([key, connection]) =>
        normalizeWhiteboardConnection(key, connection)
      )
      .filter((connection): connection is WhiteboardConnection => Boolean(connection))
      .map(connection => [connection.id, connection])
  )
}

function normalizeSnapshot(value: unknown): WhiteboardStoreSnapshot | null {
  const record = asRecord(value)
  const store = asRecord(record?.store)
  const schema = asRecord(record?.schema)

  if (!record || !store || !schema) {
    return null
  }

  return {
    store: Object.fromEntries(
      Object.entries(store).map(([key, entry]) => [key, asRecord(entry) || {}])
    ),
    schema
  }
}

function getSnapshotShapeType(record: UnknownRecord): WhiteboardShapeType {
  if (record.type === "geo") {
    const geoType = asOptionalString(asRecord(record.props)?.geo)?.toLowerCase()

    switch (geoType) {
      case "ellipse":
      case "oval":
        return "circle"
      case "diamond":
        return "diamond"
      case "triangle":
        return "triangle"
      case "cloud":
        return "cloud"
      default:
        return "rectangle"
    }
  }

  return normalizeWhiteboardShapeType(record.type)
}

function getSnapshotPointMap(value: unknown): WhiteboardPoint[] | undefined {
  const record = asRecord(value)

  if (!record) {
    return normalizePointList(value)
  }

  return Object.values(record)
    .map(point => {
      const pointRecord = asRecord(point)

      if (!pointRecord) {
        return null
      }

      return {
        index: asOptionalString(pointRecord.index) || "",
        x: asFiniteNumber(pointRecord.x, 0),
        y: asFiniteNumber(pointRecord.y, 0)
      }
    })
    .filter(
      (point): point is WhiteboardPoint & { index: string } => Boolean(point)
    )
    .sort((left, right) => left.index.localeCompare(right.index))
    .map(({ x, y }) => ({ x, y }))
}

function connectionIdFromArrowShapeId(shapeId: string) {
  const normalized = shapeId.replace(/^shape:/, "")
  return normalized.startsWith("connection:")
    ? normalized.replace(/^connection:/, "")
    : normalized
}

function toShapeRecordId(id: string) {
  return id.startsWith("shape:") ? id : `shape:${id}`
}

function toBindingRecordId(id: string) {
  return id.startsWith("binding:") ? id : `binding:${id}`
}

function toAssetRecordId(id: string) {
  return id.startsWith("asset:") ? id : `asset:${id}`
}

function toConnectionShapeRecordId(connectionId: string) {
  return toShapeRecordId(`connection:${connectionId}`)
}

function normalizeSnapshotRecordShape(
  record: UnknownRecord,
  assetsById: Map<string, UnknownRecord>
): WhiteboardShape | null {
  const id = asOptionalString(record.id)

  if (!id) {
    return null
  }

  const props = asRecord(record.props) || {}
  const assetId = asOptionalString(props.assetId)
  const imageAsset = assetId ? assetsById.get(assetId) : null
  const imageAssetProps = asRecord(imageAsset?.props) || {}

  return normalizeWhiteboardShape(id.replace(/^shape:/, ""), {
    id: id.replace(/^shape:/, ""),
    type: getSnapshotShapeType(record),
    x: asFiniteNumber(record.x, 0),
    y: asFiniteNumber(record.y, 0),
    width: asOptionalNumber(props.w),
    height: asOptionalNumber(props.h),
    rotation: asOptionalNumber(record.rotation),
    opacity: asOptionalNumber(record.opacity),
    color: asOptionalString(props.color),
    fillStyle: asOptionalString(props.fill),
    dash: asOptionalString(props.dash),
    size: asOptionalString(props.size),
    font: asOptionalString(props.font),
    textAlign: asOptionalString(props.textAlign),
    align: asOptionalString(props.align),
    verticalAlign: asOptionalString(props.verticalAlign),
    url: asOptionalString(props.url),
    fill: asOptionalString(props.fill),
    stroke: asOptionalString(props.color),
    text: asOptionalString(props.text),
    points: getSnapshotPointMap(props.points),
    start: normalizePoint(props.start),
    end: normalizePoint(props.end),
    bend: asOptionalNumber(props.bend),
    arrowheadStart: asOptionalString(props.arrowheadStart),
    arrowheadEnd: asOptionalString(props.arrowheadEnd),
    src: asOptionalString(imageAssetProps.src),
    locked: typeof record.isLocked === "boolean" ? record.isLocked : undefined
  })
}

export function extractCanvasDataFromSnapshot(
  snapshot: WhiteboardStoreSnapshot,
  {
    version = DEFAULT_CANVAS_VERSION,
    lastModified = new Date().toISOString()
  }: {
    version?: number
    lastModified?: string
  } = {}
): WhiteboardCanvasData {
  const shapes: Record<string, WhiteboardShape> = {}
  const connections: Record<string, WhiteboardConnection> = {}
  const assetsById = new Map<string, UnknownRecord>()
  const bindingsByArrowId = new Map<
    string,
    {
      start?: UnknownRecord
      end?: UnknownRecord
    }
  >()

  for (const record of Object.values(snapshot.store)) {
    if (record.typeName === "asset" && record.type === "image") {
      const id = asOptionalString(record.id)

      if (id) {
        assetsById.set(id, record)
      }
    }

    if (record.typeName === "binding" && record.type === "arrow") {
      const fromId = asOptionalString(record.fromId)
      const props = asRecord(record.props) || {}
      const terminal = asOptionalString(props.terminal)

      if (!fromId || !terminal) {
        continue
      }

      const currentBindings = bindingsByArrowId.get(fromId) || {}
      if (terminal === "start") {
        currentBindings.start = record
      }
      if (terminal === "end") {
        currentBindings.end = record
      }
      bindingsByArrowId.set(fromId, currentBindings)
    }
  }

  for (const record of Object.values(snapshot.store)) {
    if (record.typeName !== "shape") {
      continue
    }

    if (record.type === "arrow") {
      const bindings = bindingsByArrowId.get(asOptionalString(record.id) || "")
      const startTarget = asOptionalString(bindings?.start?.toId)
      const endTarget = asOptionalString(bindings?.end?.toId)

      if (startTarget && endTarget) {
        const props = asRecord(record.props) || {}
        const connectionId = connectionIdFromArrowShapeId(
          asOptionalString(record.id) || ""
        )

        const connection = normalizeWhiteboardConnection(connectionId, {
          id: connectionId,
          from_shape_id: startTarget.replace(/^shape:/, ""),
          to_shape_id: endTarget.replace(/^shape:/, ""),
          type:
            asFiniteNumber(props.bend, 0) === 0
              ? "straight"
              : "curved",
          from_anchor: anchorFromNormalized(
            asRecord(bindings?.start?.props)?.normalizedAnchor
          ),
          to_anchor: anchorFromNormalized(
            asRecord(bindings?.end?.props)?.normalizedAnchor
          ),
          color: asOptionalString(props.color),
          dash: asOptionalString(props.dash),
          size: asOptionalString(props.size),
          text: asOptionalString(props.text),
          bend: asOptionalNumber(props.bend),
          arrowheadStart: asOptionalString(props.arrowheadStart),
          arrowheadEnd: asOptionalString(props.arrowheadEnd)
        })

        if (connection) {
          connections[connection.id] = connection
          continue
        }
      }
    }

    const shape = normalizeSnapshotRecordShape(record, assetsById)

    if (shape) {
      shapes[shape.id] = shape
    }
  }

  return {
    shapes,
    connections,
    version,
    last_modified: lastModified,
    shape_count: Object.keys(shapes).length + Object.keys(connections).length,
    connection_count: Object.keys(connections).length,
    snapshot
  }
}

function anchorFromNormalized(value: unknown): WhiteboardAnchor {
  const point = normalizePoint(value)

  if (!point) {
    return "center"
  }

  if (point.x <= 0.1) return "left"
  if (point.x >= 0.9) return "right"
  if (point.y <= 0.1) return "top"
  if (point.y >= 0.9) return "bottom"

  return "center"
}

export function createEmptyWhiteboardCanvasData(
  overrides?: Partial<WhiteboardCanvasData>
): WhiteboardCanvasData {
  return {
    shapes: {},
    connections: {},
    version: overrides?.version ?? DEFAULT_CANVAS_VERSION,
    last_modified: overrides?.last_modified || new Date().toISOString(),
    shape_count: overrides?.shape_count ?? 0,
    connection_count: overrides?.connection_count ?? 0,
    snapshot: overrides?.snapshot ?? null
  }
}

export function normalizeWhiteboardCanvasData(
  value: unknown,
  {
    fallbackVersion = DEFAULT_CANVAS_VERSION,
    fallbackLastModified = new Date().toISOString()
  }: {
    fallbackVersion?: number
    fallbackLastModified?: string
  } = {}
): WhiteboardCanvasData | null {
  const record = asRecord(value)

  if (!record) {
    return null
  }

  const snapshot =
    normalizeSnapshot(record.snapshot) ||
    (record.store ? normalizeSnapshot(record) : null)

  const version = Math.max(
    1,
    Math.round(asFiniteNumber(record.version, fallbackVersion))
  )
  const lastModified =
    asOptionalString(record.last_modified) || fallbackLastModified

  const normalizedShapes = normalizeShapeCollection(record.shapes)
  const normalizedConnections = normalizeConnectionCollection(record.connections)

  if (
    snapshot &&
    Object.keys(normalizedShapes).length === 0 &&
    Object.keys(normalizedConnections).length === 0
  ) {
    return extractCanvasDataFromSnapshot(snapshot, {
      version,
      lastModified
    })
  }

  return {
    shapes: normalizedShapes,
    connections: normalizedConnections,
    version,
    last_modified: lastModified,
    shape_count:
      asOptionalNumber(record.shape_count) ??
      Object.keys(normalizedShapes).length +
        Object.keys(normalizedConnections).length,
    connection_count:
      asOptionalNumber(record.connection_count) ??
      Object.keys(normalizedConnections).length,
    snapshot
  }
}

function getBaseStoreRecords(snapshot?: WhiteboardStoreSnapshot | null) {
  const store = snapshot?.store || {}

  return Object.fromEntries(
    Object.entries(store).filter(([, value]) => {
      if (value.typeName === "shape") return false
      if (value.typeName === "binding") return false
      if (value.typeName === "asset") return false
      return true
    })
  )
}

function getPageId(
  snapshot?: WhiteboardStoreSnapshot | null,
  providedPageId?: string | null
) {
  if (providedPageId) {
    return providedPageId
  }

  const existingPageId = Object.values(snapshot?.store || {}).find(
    value => value.typeName === "page"
  )?.id

  return asOptionalString(existingPageId) || "page:page"
}

function normalizeConnectionAnchorPoint(anchor?: WhiteboardAnchor): WhiteboardPoint {
  switch (anchor) {
    case "top":
      return { x: 0.5, y: 0 }
    case "right":
      return { x: 1, y: 0.5 }
    case "bottom":
      return { x: 0.5, y: 1 }
    case "left":
      return { x: 0, y: 0.5 }
    default:
      return { x: 0.5, y: 0.5 }
  }
}

function getShapeBounds(shape: WhiteboardShape) {
  const width =
    shape.width ??
    (shape.type === "text"
      ? DEFAULT_TEXT_WIDTH
      : shape.type === "note"
        ? DEFAULT_NOTE_WIDTH
        : shape.type === "image"
          ? DEFAULT_IMAGE_WIDTH
          : DEFAULT_BOX_WIDTH)
  const height =
    shape.height ??
    (shape.type === "note"
      ? DEFAULT_NOTE_HEIGHT
      : shape.type === "image"
        ? DEFAULT_IMAGE_HEIGHT
        : DEFAULT_BOX_HEIGHT)

  return {
    x: shape.x,
    y: shape.y,
    width,
    height
  }
}

function getAnchorPoint(
  shape: WhiteboardShape,
  anchor: WhiteboardAnchor = "center"
): WhiteboardPoint {
  const bounds = getShapeBounds(shape)
  const normalized = normalizeConnectionAnchorPoint(anchor)

  return {
    x: bounds.x + bounds.width * normalized.x,
    y: bounds.y + bounds.height * normalized.y
  }
}

function buildLinePoints(points?: WhiteboardPoint[]) {
  const normalizedPoints =
    points && points.length > 0
      ? points
      : [
          { x: 0, y: 0 },
          { x: DEFAULT_ARROW_DELTA.x, y: DEFAULT_ARROW_DELTA.y }
        ]

  const indices = getIndices(normalizedPoints.length)

  return Object.fromEntries(
    normalizedPoints.map((point, index) => [
      indices[index],
      {
        id: indices[index],
        index: indices[index],
        x: point.x,
        y: point.y
      }
    ])
  )
}

function buildShapeRecord(
  shape: WhiteboardShape,
  pageId: string,
  index: string
) {
  const color = shape.color || normalizeWhiteboardColor(shape.stroke, "black")
  const fillStyle = shape.fillStyle || normalizeWhiteboardFillStyle(shape.fill, "none")
  const dash = shape.dash || normalizeWhiteboardDashStyle(undefined, "draw")
  const size = shape.size || normalizeWhiteboardSize(shape.strokeWidth, "m")
  const font = shape.font || normalizeWhiteboardFont(shape.fontFamily, "draw")
  const baseRecord = {
    id: toShapeRecordId(shape.id),
    typeName: "shape",
    parentId: pageId,
    index,
    x: shape.x,
    y: shape.y,
    rotation: shape.rotation || 0,
    isLocked: Boolean(shape.locked),
    opacity: shape.opacity ?? 1,
    meta: {}
  }

  switch (shape.type) {
    case "rectangle":
    case "circle":
    case "diamond":
    case "triangle":
    case "cloud":
      return {
        ...baseRecord,
        type: "geo",
        props: {
          geo:
            shape.type === "circle"
              ? "ellipse"
              : shape.type,
          labelColor: color,
          color,
          fill: fillStyle,
          dash,
          size,
          font,
          align: shape.align || "middle",
          verticalAlign: shape.verticalAlign || "middle",
          url: shape.url || "",
          w: shape.width || DEFAULT_BOX_WIDTH,
          h: shape.height || DEFAULT_BOX_HEIGHT,
          growY: 0,
          text: shape.text || "",
          scale: 1
        }
      }
    case "note":
      return {
        ...baseRecord,
        type: "note",
        props: {
          color,
          size,
          font,
          fontSizeAdjustment: 0,
          align: shape.align || "middle",
          verticalAlign: shape.verticalAlign || "middle",
          growY: 0,
          url: shape.url || "",
          text: shape.text || "",
          scale: 1
        }
      }
    case "text":
      return {
        ...baseRecord,
        type: "text",
        props: {
          color,
          size,
          font,
          textAlign: shape.textAlign || "start",
          w: shape.width || DEFAULT_TEXT_WIDTH,
          text: shape.text || "",
          scale: 1,
          autoSize: !shape.width
        }
      }
    case "line":
      return {
        ...baseRecord,
        type: "line",
        props: {
          color,
          dash,
          size,
          spline: "line",
          points: buildLinePoints(shape.points),
          scale: 1
        }
      }
    case "arrow": {
      const start = shape.start || { x: 0, y: 0 }
      const end =
        shape.end ||
        (shape.points && shape.points.length > 1
          ? shape.points[shape.points.length - 1]
          : {
              x: shape.x + DEFAULT_ARROW_DELTA.x,
              y: shape.y + DEFAULT_ARROW_DELTA.y
            })

      return {
        ...baseRecord,
        type: "arrow",
        props: {
          labelColor: color,
          color,
          fill: "none",
          dash,
          size,
          arrowheadStart: shape.arrowheadStart || "none",
          arrowheadEnd: shape.arrowheadEnd || "arrow",
          font,
          start: {
            x: start.x - shape.x,
            y: start.y - shape.y
          },
          end: {
            x: end.x - shape.x,
            y: end.y - shape.y
          },
          bend: shape.bend || 0,
          text: shape.text || "",
          labelPosition: 0.5,
          scale: 1
        }
      }
    }
    case "image":
      return {
        ...baseRecord,
        type: "image",
        props: {
          w: shape.width || DEFAULT_IMAGE_WIDTH,
          h: shape.height || DEFAULT_IMAGE_HEIGHT,
          playing: false,
          url: shape.src || shape.url || "",
          assetId: shape.src ? toAssetRecordId(shape.id) : null,
          crop: null,
          flipX: false,
          flipY: false
        }
      }
  }
}

function buildImageAssetRecord(shape: WhiteboardShape) {
  if (shape.type !== "image" || !shape.src) {
    return null
  }

  return {
    id: toAssetRecordId(shape.id),
    typeName: "asset",
    type: "image",
    meta: {},
    props: {
      w: shape.width || DEFAULT_IMAGE_WIDTH,
      h: shape.height || DEFAULT_IMAGE_HEIGHT,
      name: shape.text || `${shape.id}.png`,
      isAnimated: false,
      mimeType: null,
      src: shape.src,
      fileSize: undefined
    }
  }
}

function buildConnectionRecords(
  connection: WhiteboardConnection,
  shapesById: Record<string, WhiteboardShape>,
  pageId: string,
  index: string
) {
  const fromShape = shapesById[connection.from_shape_id]
  const toShape = shapesById[connection.to_shape_id]

  if (!fromShape || !toShape) {
    return null
  }

  const arrowId = toConnectionShapeRecordId(connection.id)
  const color = connection.color || normalizeWhiteboardColor(connection.stroke, "black")
  const size = connection.size || normalizeWhiteboardSize(connection.strokeWidth, "m")
  const dash = connection.dash || "draw"
  const fromPoint = getAnchorPoint(fromShape, connection.from_anchor || "center")
  const toPoint = getAnchorPoint(toShape, connection.to_anchor || "center")

  return {
    shape: {
      id: arrowId,
      typeName: "shape",
      type: "arrow",
      parentId: pageId,
      index,
      x: (fromPoint.x + toPoint.x) / 2,
      y: (fromPoint.y + toPoint.y) / 2,
      rotation: 0,
      isLocked: false,
      opacity: 1,
      meta: {},
      props: {
        labelColor: color,
        color,
        fill: "none",
        dash,
        size,
        arrowheadStart: connection.arrowheadStart || "none",
        arrowheadEnd: connection.arrowheadEnd || "arrow",
        font: "draw",
        start: { x: 0, y: 0 },
        end: { x: 0, y: 0 },
        bend:
          connection.type === "curved"
            ? connection.bend || 60
            : 0,
        text: connection.text || "",
        labelPosition: 0.5,
        scale: 1
      }
    },
    bindings: [
      {
        id: toBindingRecordId(`${connection.id}-start`),
        typeName: "binding",
        type: "arrow",
        fromId: arrowId,
        toId: toShapeRecordId(connection.from_shape_id),
        meta: {},
        props: {
          terminal: "start",
          normalizedAnchor: normalizeConnectionAnchorPoint(
            connection.from_anchor
          ),
          isExact: false,
          isPrecise: true
        }
      },
      {
        id: toBindingRecordId(`${connection.id}-end`),
        typeName: "binding",
        type: "arrow",
        fromId: arrowId,
        toId: toShapeRecordId(connection.to_shape_id),
        meta: {},
        props: {
          terminal: "end",
          normalizedAnchor: normalizeConnectionAnchorPoint(
            connection.to_anchor
          ),
          isExact: false,
          isPrecise: true
        }
      }
    ]
  }
}

export function buildWhiteboardStoreSnapshot(
  canvasData: WhiteboardCanvasData,
  options?: {
    baseSnapshot?: WhiteboardStoreSnapshot | null
    pageId?: string | null
  }
): WhiteboardStoreSnapshot {
  const normalizedCanvasData =
    normalizeWhiteboardCanvasData(canvasData) || createEmptyWhiteboardCanvasData()

  if (normalizedCanvasData.snapshot) {
    return normalizedCanvasData.snapshot
  }

  const baseSnapshot = options?.baseSnapshot || null
  const pageId = getPageId(baseSnapshot, options?.pageId)
  const store = getBaseStoreRecords(baseSnapshot)
  const shapes = Object.values(normalizedCanvasData.shapes).sort((left, right) => {
    const leftOrder = left.z_index ?? 0
    const rightOrder = right.z_index ?? 0
    return leftOrder - rightOrder
  })
  const totalRecordCount =
    shapes.length + Object.keys(normalizedCanvasData.connections).length
  const indices = getIndices(Math.max(totalRecordCount, 1))

  shapes.forEach((shape, index) => {
    const asset = buildImageAssetRecord(shape)

    if (asset) {
      store[asset.id] = asset
    }

    const record = buildShapeRecord(shape, pageId, indices[index])
    store[record.id] = record
  })

  Object.values(normalizedCanvasData.connections).forEach((connection, index) => {
    const connectionRecords = buildConnectionRecords(
      connection,
      normalizedCanvasData.shapes,
      pageId,
      indices[shapes.length + index]
    )

    if (!connectionRecords) {
      return
    }

    store[connectionRecords.shape.id] = connectionRecords.shape

    connectionRecords.bindings.forEach(binding => {
      store[binding.id] = binding
    })
  })

  return {
    store,
    schema: baseSnapshot?.schema || {
      schemaVersion: 2,
      sequences: {}
    }
  }
}

export function countWhiteboardShapes(canvasData: unknown) {
  const normalizedCanvasData = normalizeWhiteboardCanvasData(canvasData)

  if (!normalizedCanvasData) {
    return 0
  }

  if (typeof normalizedCanvasData.shape_count === "number") {
    return normalizedCanvasData.shape_count
  }

  if (normalizedCanvasData.snapshot) {
    return Object.values(normalizedCanvasData.snapshot.store).filter(
      value => value.typeName === "shape"
    ).length
  }

  return (
    Object.keys(normalizedCanvasData.shapes).length +
    Object.keys(normalizedCanvasData.connections).length
  )
}
